import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { FileSearch, Search, Filter, CheckCircle2, AlertTriangle, XCircle, Eye, X, ChevronLeft, ChevronRight, Wrench, Download, FileSpreadsheet, Loader2, Route, RefreshCw, ChevronDown, CornerDownLeft } from 'lucide-react'
import RequirePermission from '../../components/auth/RequirePermission.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { ActionButton, Section } from '../../components/admin/ai/ui.js'
import { formatDate, formatDuration } from '../../utils/formatDate.js'
import { formatInTimezone } from '../../utils/timezone.js'
import Select from '../../components/ui/Select.js'

interface ToolCall {
  tool: string
  round: number
  success: boolean
  error?: string
}

interface Tier1Candidate {
  side: string
  member: string
  matchedKeyword: string
  keywordLength: number
}

interface Tier2Classification {
  intent: string
  agentType: string
  confidence: number
  summary: string
}

/** One stage of the two-stage routing trace (side selection or member selection). */
interface StageRoutingTrace {
  tierResolved: number | null
  resolved: string | null
  confidence: number | null
  candidates: Tier1Candidate[]
  classification:
    | { side: string; confidence: number; summary: string }
    | { member: string; confidence: number; summary: string }
    | Tier2Classification
    | null
  fallbackReason: string | null
}

/**
 * Two-stage routing trace (current shape). Rows written before the two-stage
 * refactor carry the legacy flat shape, so those fields stay optional for
 * backward-compatible rendering of historical rows.
 */
interface RoutingTrace {
  sideRouting?: StageRoutingTrace
  agentRouting?: StageRoutingTrace | null
  /** @deprecated legacy flat shape — pre-refactor rows only. */
  tierResolved?: number
  /** @deprecated legacy flat shape. */
  tier1Candidates?: Tier1Candidate[]
  /** @deprecated legacy flat shape. */
  tier2Classification?: Tier2Classification | null
  /** @deprecated legacy flat shape. */
  fallbackReason?: string | null
}

interface PromptSourceTrace {
  orchestratorSlug?: string
  agentSlug?: string
  agentPromptId?: string
  agentPromptOrigin?: string
  agentPromptVersion?: number
  guardrailIds?: string[]
  usedFallback: boolean
}

interface AuditItem {
  id: string
  timestamp: string
  agentSlug: string
  agentName: string
  channel: string
  provider: string
  model: string
  status: 'success' | 'error' | 'blocked'
  promptTokens: number
  completionTokens: number
  latencyMs: number
  userQuery: string
  aiResponse: string
  toolsCalled?: ToolCall[]
  policyViolation?: string
  routingTrace?: RoutingTrace
  promptSource?: PromptSourceTrace
}

interface AuditResponse {
  items: AuditItem[]
  total: number
  page: number
  limit: number
  pages: number
}

/** Everything the list endpoint is queried with — one object so a change to
 *  any part of it goes through a single code path (`applyQuery`). */
interface AuditQuery {
  search: string
  entity: string
  status: string
  provider: string
  model: string
  channel: string
  page: number
  limit: number
}

const ALL = 'all'
const DEFAULT_LIMIT = 20
const PAGE_SIZES = [10, 20, 50] as const

const DEFAULT_QUERY: AuditQuery = {
  search: '',
  entity: ALL,
  status: ALL,
  provider: ALL,
  model: ALL,
  channel: ALL,
  page: 1,
  limit: DEFAULT_LIMIT,
}

interface FilterOption { value: string; label: string }

const ENTITY_OPTIONS: FilterOption[] = [
  { value: ALL, label: 'All Event Types' },
  { value: 'interaction', label: 'AI Interactions' },
  { value: 'orchestrator', label: 'Orchestrator History' },
  { value: 'safety', label: 'Safety Events' },
  { value: 'generation', label: 'Generation & Embedding' },
]
const STATUS_OPTIONS: FilterOption[] = [
  { value: ALL, label: 'All Statuses' },
  { value: 'success', label: 'Success' },
  { value: 'blocked', label: 'Blocked (Safety)' },
  { value: 'error', label: 'Error / Timeout' },
]
const PROVIDER_OPTIONS: FilterOption[] = [
  { value: ALL, label: 'All Providers' },
  { value: 'ollama', label: 'Ollama' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'groq', label: 'Groq' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google', label: 'Google' },
]
const CHANNEL_OPTIONS: FilterOption[] = [
  { value: ALL, label: 'All Channels' },
  { value: 'web', label: 'Web' },
  { value: 'agent_portal', label: 'Agent Portal' },
  { value: 'api', label: 'API' },
  { value: 'voice', label: 'Voice' },
  { value: 'orchestrator', label: 'Orchestrator' },
]

/** Only the filter facets — page/limit are navigation, not filtering. */
const FILTER_KEYS = ['search', 'entity', 'status', 'provider', 'model', 'channel'] as const

function toSearchParams(q: AuditQuery, extra?: Record<string, string>): URLSearchParams {
  const params = new URLSearchParams()
  if (q.search.trim()) params.set('search', q.search.trim())
  for (const key of ['entity', 'status', 'provider', 'model', 'channel'] as const) {
    if (q[key] !== ALL) params.set(key, q[key])
  }
  for (const [k, v] of Object.entries(extra ?? {})) params.set(k, v)
  return params
}

/** `71526` → "71.5 s"; `842` → "842 ms"; ≥ 1 min falls back to the shared h/m/s formatter. */
function formatLatency(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—'
  if (ms < 1000) return `${Math.round(ms)} ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`
  return formatDuration(ms)
}

function formatTime(input: string): string {
  return formatInTimezone(input, { hour: '2-digit', minute: '2-digit', second: '2-digit' }, undefined, '—')
}

function humanize(value: string): string {
  return value.replace(/_/g, ' ')
}

const STATUS_BADGE: Record<AuditItem['status'], { label: string; className: string; icon: typeof CheckCircle2 }> = {
  success: { label: 'Success', className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', icon: CheckCircle2 },
  blocked: { label: 'Blocked', className: 'bg-amber-50 text-amber-700 ring-amber-600/20', icon: AlertTriangle },
  error: { label: 'Error', className: 'bg-red-50 text-red-700 ring-red-600/20', icon: XCircle },
}

function StatusBadge({ status }: { status: AuditItem['status'] }): JSX.Element {
  const meta = STATUS_BADGE[status] ?? STATUS_BADGE.error
  const Icon = meta.icon
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${meta.className}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  )
}

function FilterSelect({
  value,
  options,
  onChange,
}: {
  value: string
  options: FilterOption[]
  onChange: (next: string) => void
}): JSX.Element {
  return (
        <Select
      ariaLabel={"Value"}
      size="sm"
      value={value}
      onChange={(v) => onChange(v)}
      className="h-9"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
        </Select>
  )
}

const COLUMN_COUNT = 8

function SkeletonRows({ rows }: { rows: number }): JSX.Element {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <tr key={i} className="animate-pulse">
          {Array.from({ length: COLUMN_COUNT }, (_, j) => (
            <td key={j} className="px-3 py-3.5">
              <div className={`h-3 rounded bg-gray-100 ${j === 3 ? 'w-full' : j === 0 ? 'w-16' : 'w-20'}`} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

export default function AiAuditPage(): JSX.Element {
  const toast = useToast()
  const [logs, setLogs] = useState<AuditItem[]>([])
  const [selectedLog, setSelectedLog] = useState<AuditItem | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState<AuditQuery>(DEFAULT_QUERY)
  // The search box is typed into freely and only becomes part of `query` on
  // Enter — so it needs its own draft state.
  const [searchDraft, setSearchDraft] = useState('')
  const [total, setTotal] = useState(0)
  const [pages, setPages] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const exportRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async (q: AuditQuery) => {
    setLoading(true)
    try {
      const params = toSearchParams(q, { page: String(q.page), limit: String(q.limit) })
      const res = await api.get<AuditResponse>(`/admin/ai/audit?${params.toString()}`)
      setLogs(res.items ?? [])
      setTotal(res.total ?? 0)
      setPages(res.pages ?? 0)
    } catch (err) {
      toast.error('Failed to load AI audit logs', String(err))
    } finally {
      setLoading(false)
    }
  }, [toast])

  /**
   * The one way the list is re-queried. A filter change resets to page 1
   * (the caller passes `page` explicitly only when paginating), and the
   * fetch is issued from the merged value rather than from state so there
   * is never a stale-closure or double-fetch window.
   */
  const applyQuery = useCallback((patch: Partial<AuditQuery>) => {
    setQuery((prev) => {
      const next: AuditQuery = { ...prev, ...patch, page: patch.page ?? 1 }
      void load(next)
      return next
    })
  }, [load])

  const handleRefresh = useCallback(() => { void load(query) }, [load, query])

  const clearFilters = useCallback(() => {
    setSearchDraft('')
    applyQuery({ ...DEFAULT_QUERY, limit: query.limit })
  }, [applyQuery, query.limit])

  const activeFilterCount = useMemo(
    () => FILTER_KEYS.filter((k) => query[k] !== DEFAULT_QUERY[k]).length,
    [query],
  )

  const handleExport = useCallback(async (format: 'csv' | 'xlsx') => {
    setExporting(true)
    try {
      const params = toSearchParams(query, { format })
      const response = await fetch(`/api/admin/ai/audit?${params.toString()}`, { credentials: 'include' })
      if (!response.ok) throw new Error(`Export failed: ${response.status}`)
      const blob = await response.blob()
      const downloadUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `ai-audit-${new Date().toISOString().slice(0, 10)}.${format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(downloadUrl)
      toast.success('Audit log exported', `${total} records downloaded as ${format.toUpperCase()}`)
    } catch (err) {
      toast.error('Export failed', String(err))
    } finally {
      setExporting(false)
    }
  }, [query, total, toast])

  const handleInspect = useCallback(async (log: AuditItem) => {
    // Show the row data immediately for instant feedback
    setSelectedLog(log)
    setDetailLoading(true)
    try {
      const detail = await api.get<AuditItem>(`/admin/ai/audit/${log.id}`)
      setSelectedLog(detail)
    } catch {
      // Keep the list row data if the detail fetch fails
    } finally {
      setDetailLoading(false)
    }
  }, [])

  // Initial load only — every later fetch goes through `applyQuery`/`handleRefresh`.
  useEffect(() => { void load(DEFAULT_QUERY) }, [load])

  // Close the export dropdown on any click outside it (same contract as
  // ReportExportMenu — the trigger itself is inside `exportRef`, so clicking
  // it still routes through the toggle instead of double-handling).
  useEffect(() => {
    if (!exportOpen) return
    const onDown = (e: MouseEvent): void => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) setExportOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [exportOpen])

  const { page, limit } = query
  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1
  const rangeEnd = Math.min(page * limit, total)
  const lastPage = pages || 1
  const canExport = !exporting && total > 0

  return (
    <RequirePermission permission="admin:extensions:view">
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-sm font-semibold text-gray-900">Audit Log</h1>
            <p className="mt-0.5 text-xs text-gray-500">
              Every AI interaction, orchestrator run, and safety event — with its prompt, response, routing trace and cost.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ActionButton
              tone="secondary"
              icon={<RefreshCw className="h-4 w-4" />}
              label="Refresh"
              busy={loading}
              onClick={handleRefresh}
            />

            <div ref={exportRef} className="relative">
              <button
                type="button"
                onClick={() => setExportOpen((v) => !v)}
                disabled={!canExport}
                aria-haspopup="menu"
                aria-expanded={exportOpen}
                title={total === 0 ? 'No AI audit events to export' : 'Export audit log'}
                className="inline-flex h-9 items-center gap-2 rounded-sm bg-[#3454cf] px-3.5 text-[13px] font-medium text-white shadow-sm transition-all hover:bg-[#2a45b0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3454cf]/40 disabled:pointer-events-none disabled:opacity-50"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {exporting ? 'Exporting…' : 'Export'}
                <ChevronDown className={`h-3.5 w-3.5 opacity-80 transition-transform ${exportOpen ? 'rotate-180' : ''}`} />
              </button>

              {exportOpen && (
                <div role="menu" className="absolute right-0 top-full z-40 mt-1.5 w-48 overflow-hidden rounded-sm border border-gray-200 bg-white py-1 shadow-xl shadow-black/5">
                  <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-gray-400">
                    Download {total.toLocaleString()} records
                  </p>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { setExportOpen(false); void handleExport('csv') }}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <Download className="h-3.5 w-3.5 text-gray-400" />
                    CSV (.csv)
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { setExportOpen(false); void handleExport('xlsx') }}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <FileSpreadsheet className="h-3.5 w-3.5 text-gray-400" />
                    Excel (.xlsx)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="rounded-sm border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex h-9 min-w-[280px] flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 transition-colors focus-within:border-[#3454cf] focus-within:ring-2 focus-within:ring-[#3454cf]/20">
              <Search className="h-4 w-4 shrink-0 text-gray-400" />
              <input
                value={searchDraft}
                onChange={(e) => {
                  const next = e.target.value
                  setSearchDraft(next)
                  // Emptying the box (backspace / select-all-delete) reverts to
                  // the unfiltered list immediately — nobody presses Enter to
                  // search for nothing.
                  if (next === '' && query.search !== '') applyQuery({ search: '' })
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') applyQuery({ search: searchDraft }) }}
                placeholder="Search query, response text, model, or agent…"
                aria-label="Search audit log"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
              />
              {searchDraft ? (
                <button
                  type="button"
                  onClick={() => { setSearchDraft(''); applyQuery({ search: '' }) }}
                  aria-label="Clear search"
                  className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <span className="hidden items-center gap-1 text-[10px] text-gray-400 sm:inline-flex">
                  <CornerDownLeft className="h-3 w-3" /> Enter
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <FilterSelect value={query.entity} options={ENTITY_OPTIONS} onChange={(v) => applyQuery({ entity: v })} />
              <FilterSelect value={query.status} options={STATUS_OPTIONS} onChange={(v) => applyQuery({ status: v })} />
              <FilterSelect value={query.provider} options={PROVIDER_OPTIONS} onChange={(v) => applyQuery({ provider: v })} />
              <FilterSelect value={query.channel} options={CHANNEL_OPTIONS} onChange={(v) => applyQuery({ channel: v })} />
            </div>

            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
              >
                <X className="h-3.5 w-3.5" />
                Clear {activeFilterCount === 1 ? 'filter' : `${activeFilterCount} filters`}
              </button>
            )}
          </div>
        </div>

        {/* Audit Log Table */}
        <Section
          icon={<FileSearch className="h-4 w-4" />}
          title="Audit Event Stream"
          description={
            loading && logs.length === 0
              ? 'Loading audit trail…'
              : total === 0
                ? 'No events match the current filters.'
                : `Showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${total.toLocaleString()} events`
          }
          className="!mb-0"
        >
          <div className="-mx-5 -my-5">
            {!loading && logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                  <FileSearch className="h-6 w-6" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">No audit events found</p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {activeFilterCount > 0
                      ? 'Try widening your search or clearing a filter.'
                      : 'Events will appear here as soon as an agent handles a request.'}
                  </p>
                </div>
                {activeFilterCount > 0 && (
                  <ActionButton tone="secondary" icon={<X className="h-4 w-4" />} label="Clear filters" onClick={clearFilters} />
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[960px] text-left text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/60 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      <th className="px-3 py-2.5 pl-5">Status</th>
                      <th className="px-3 py-2.5">Timestamp</th>
                      <th className="px-3 py-2.5">Agent</th>
                      <th className="px-3 py-2.5">User Query</th>
                      <th className="px-3 py-2.5">Model</th>
                      <th className="px-3 py-2.5 text-right">Latency</th>
                      <th className="px-3 py-2.5 text-right">Tokens</th>
                      <th className="px-3 py-2.5 pr-5 text-right">
                        <span className="sr-only">Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y divide-gray-100 ${loading ? 'opacity-60' : ''}`}>
                    {loading && logs.length === 0 ? (
                      <SkeletonRows rows={Math.min(limit, 8)} />
                    ) : (
                      logs.map((log) => {
                        const tokens = log.promptTokens + log.completionTokens
                        return (
                          <tr
                            key={log.id}
                            onClick={() => void handleInspect(log)}
                            className="group cursor-pointer transition-colors hover:bg-[#3454cf]/[0.03]"
                          >
                            <td className="px-3 py-3 pl-5">
                              <StatusBadge status={log.status} />
                            </td>
                            <td className="whitespace-nowrap px-3 py-3" title={log.timestamp}>
                              <div className="font-mono tabular-nums text-gray-900">{formatTime(log.timestamp)}</div>
                              <div className="mt-0.5 text-[11px] text-gray-400">{formatDate(log.timestamp)}</div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-3">
                              <div className="font-semibold text-gray-900">{log.agentName}</div>
                              <div className="mt-0.5 text-[11px] capitalize text-gray-400">{humanize(log.channel)}</div>
                            </td>
                            <td className="max-w-xs px-3 py-3">
                              <p className="truncate text-gray-700" title={log.userQuery}>{log.userQuery}</p>
                            </td>
                            <td className="whitespace-nowrap px-3 py-3">
                              <div className="font-mono text-[11px] text-gray-700">{log.model}</div>
                              <div className="mt-0.5 text-[11px] capitalize text-gray-400">{log.provider}</div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-3 text-right font-mono tabular-nums text-gray-700" title={`${log.latencyMs.toLocaleString()} ms`}>
                              {formatLatency(log.latencyMs)}
                            </td>
                            <td
                              className="whitespace-nowrap px-3 py-3 text-right font-mono tabular-nums text-gray-700"
                              title={`${log.promptTokens.toLocaleString()} prompt + ${log.completionTokens.toLocaleString()} completion`}
                            >
                              {tokens.toLocaleString()}
                            </td>
                            <td className="px-3 py-3 pr-5 text-right">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); void handleInspect(log) }}
                                className="inline-flex items-center gap-1 rounded-sm border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:border-[#3454cf]/40 hover:bg-[#3454cf]/5 hover:text-[#3454cf] group-hover:border-gray-300"
                              >
                                <Eye className="h-3 w-3" /> Inspect
                              </button>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination footer */}
            {total > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/40 px-5 py-3">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="tabular-nums">
                    {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of {total.toLocaleString()}
                  </span>
                  <span className="text-gray-300">·</span>
                                    <Select
                    ariaLabel="Rows per page"
                    size="sm"
                    value={String(limit)}
                    onChange={(v) => applyQuery({ limit: Number(v) })}
                    className="h-7"
                  >
                    {PAGE_SIZES.map((n) => (
                      <option key={n} value={String(n)}>{n} / page</option>
                    ))}
                                    </Select>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => applyQuery({ page: Math.max(1, page - 1) })}
                    disabled={loading || page <= 1}
                    className="inline-flex h-7 items-center gap-1 rounded border border-gray-200 bg-white px-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
                  >
                    <ChevronLeft className="h-3 w-3" /> Prev
                  </button>
                  <span className="px-2 text-xs tabular-nums text-gray-600">
                    Page {page} of {lastPage}
                  </span>
                  <button
                    type="button"
                    onClick={() => applyQuery({ page: Math.min(lastPage, page + 1) })}
                    disabled={loading || page >= lastPage}
                    className="inline-flex h-7 items-center gap-1 rounded border border-gray-200 bg-white px-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
                  >
                    Next <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </Section>
        {/* Inspect Modal */}
        {selectedLog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="flex w-full max-w-2xl flex-col rounded-xl bg-white p-6 shadow-xl max-h-[90vh]">
              <div className="flex shrink-0 items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-bold text-gray-900">AI Interaction Trace: {selectedLog.id}</h2>
                  {detailLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
                </div>
                <button onClick={() => { setSelectedLog(null); setDetailLoading(false) }} className="text-gray-400 hover:text-gray-700">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-4 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto text-xs">
                <div className="grid grid-cols-3 gap-2 rounded-lg bg-gray-50 p-3 font-mono">
                  <div>
                    <span className="text-gray-400 text-[10px] uppercase">Agent</span>
                    <p className="font-semibold text-gray-800">{selectedLog.agentName}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-[10px] uppercase">Provider / Model</span>
                    <p className="font-semibold text-gray-800">{selectedLog.provider} / {selectedLog.model}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-[10px] uppercase">Tokens & Latency</span>
                    <p className="font-semibold text-gray-800">{selectedLog.promptTokens + selectedLog.completionTokens} tokens | {selectedLog.latencyMs}ms</p>
                  </div>
                </div>

                <div>
                  <span className="font-semibold text-gray-700">User Input Query:</span>
                  <p className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-gray-900">
                    {selectedLog.userQuery}
                  </p>
                </div>

                <div>
                  <span className="font-semibold text-gray-700">AI System Response:</span>
                  <p className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-gray-900">
                    {selectedLog.aiResponse}
                  </p>
                </div>

                {selectedLog.toolsCalled && selectedLog.toolsCalled.length > 0 && (
                  <div>
                    <span className="font-semibold text-gray-700">Tools Invoked:</span>
                    <div className="mt-1 space-y-1.5">
                      {selectedLog.toolsCalled.map((t) => (
                        <div
                          key={`${t.tool}-${t.round}`}
                          className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                        >
                          <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-gray-800">
                            <Wrench className="h-3 w-3 text-blue-500" />
                            {t.tool}
                          </span>
                          <span className="text-[10px] text-gray-500">round {t.round}</span>
                          {t.success ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600">
                              <CheckCircle2 className="h-3 w-3" /> Success
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-medium text-red-600"
                              title={t.error}
                            >
                              <XCircle className="h-3 w-3" /> Error
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedLog.routingTrace && (
                  <div>
                    <span className="font-semibold text-gray-700">Routing Trace:</span>
                    <div className="mt-1 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      {selectedLog.routingTrace.sideRouting || selectedLog.routingTrace.agentRouting ? (
                        <>
                          {(
                            [
                              ['Stage 1 — Side Orchestrator', selectedLog.routingTrace.sideRouting],
                              ['Stage 2 — Member Agent', selectedLog.routingTrace.agentRouting],
                            ] as Array<[string, StageRoutingTrace | null | undefined]>
                          ).map(([label, stage]) =>
                            stage ? (
                              <div key={label}>
                                <div className="flex items-center gap-2">
                                  <Route className="h-3.5 w-3.5 text-blue-500" />
                                  <span className="font-mono text-[11px] text-gray-800">
                                    {label}: {stage.tierResolved !== null ? `Tier ${stage.tierResolved}` : 'unresolved'}
                                    {stage.resolved && <span className="ml-1 text-blue-700">→ {stage.resolved}</span>}
                                    {stage.fallbackReason && (
                                      <span className="ml-2 text-gray-500">
                                        (fallback: {stage.fallbackReason.replace(/_/g, ' ')})
                                      </span>
                                    )}
                                  </span>
                                </div>

                                {stage.candidates.length > 0 && (
                                  <div className="mt-1">
                                    <span className="text-[10px] uppercase text-gray-400">Tier 1 — Keyword Candidates</span>
                                    <div className="mt-1 space-y-1">
                                      {stage.candidates.map((c, i) => (
                                        <div
                                          key={`${c.side}-${c.member}-${i}`}
                                          className="flex items-center justify-between rounded border border-gray-200 bg-white px-2 py-1 font-mono text-[10px] text-gray-700"
                                        >
                                          <span>{c.side} / {c.member}</span>
                                          <span className="text-blue-600">"{c.matchedKeyword}"</span>
                                          <span className="text-gray-400">{c.keywordLength} chars</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {stage.classification && (
                                  <div className="mt-1">
                                    <span className="text-[10px] uppercase text-gray-400">Tier 2 — LLM Classification</span>
                                    <div className="mt-1 rounded border border-gray-200 bg-white px-2 py-1.5 font-mono text-[10px] text-gray-700">
                                      <div className="flex justify-between">
                                        <span>Choice:</span>
                                        <span className="text-gray-900">
                                          {'side' in stage.classification
                                            ? stage.classification.side
                                            : 'member' in stage.classification
                                              ? stage.classification.member
                                              : `${stage.classification.intent} (${stage.classification.agentType})`}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span>Confidence:</span>
                                        <span className="text-gray-900">{(stage.classification.confidence * 100).toFixed(0)}%</span>
                                      </div>
                                      {stage.classification.summary && (
                                        <div className="mt-1 border-t border-gray-100 pt-1 text-gray-500">
                                          {stage.classification.summary}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : null,
                          )}
                        </>
                      ) : (
                        /* Legacy flat trace written before the two-stage refactor. */
                        <>
                          <div className="flex items-center gap-2">
                            <Route className="h-3.5 w-3.5 text-blue-500" />
                            <span className="font-mono text-[11px] text-gray-800">
                              Resolved at Tier {selectedLog.routingTrace.tierResolved}
                              {selectedLog.routingTrace.fallbackReason && (
                                <span className="ml-2 text-gray-500">
                                  (fallback: {selectedLog.routingTrace.fallbackReason.replace(/_/g, ' ')})
                                </span>
                              )}
                            </span>
                          </div>

                          {(selectedLog.routingTrace.tier1Candidates?.length ?? 0) > 0 && (
                            <div>
                              <span className="text-[10px] uppercase text-gray-400">Tier 1 — Keyword Candidates</span>
                              <div className="mt-1 space-y-1">
                                {selectedLog.routingTrace.tier1Candidates!.map((c, i) => (
                                  <div
                                    key={`${c.side}-${c.member}-${i}`}
                                    className="flex items-center justify-between rounded border border-gray-200 bg-white px-2 py-1 font-mono text-[10px] text-gray-700"
                                  >
                                    <span>{c.side} / {c.member}</span>
                                    <span className="text-blue-600">"{c.matchedKeyword}"</span>
                                    <span className="text-gray-400">{c.keywordLength} chars</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {selectedLog.routingTrace.tier2Classification && (
                            <div>
                              <span className="text-[10px] uppercase text-gray-400">Tier 2 — LLM Classification</span>
                              <div className="mt-1 rounded border border-gray-200 bg-white px-2 py-1.5 font-mono text-[10px] text-gray-700">
                                <div className="flex justify-between">
                                  <span>Intent:</span>
                                  <span className="text-gray-900">{selectedLog.routingTrace.tier2Classification.intent}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Agent Type:</span>
                                  <span className="text-gray-900">{selectedLog.routingTrace.tier2Classification.agentType}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Confidence:</span>
                                  <span className="text-gray-900">{(selectedLog.routingTrace.tier2Classification.confidence * 100).toFixed(0)}%</span>
                                </div>
                                {selectedLog.routingTrace.tier2Classification.summary && (
                                  <div className="mt-1 border-t border-gray-100 pt-1 text-gray-500">
                                    {selectedLog.routingTrace.tier2Classification.summary}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {selectedLog.promptSource && (
                  <div>
                    <span className="font-semibold text-gray-700">Prompt Source:</span>
                    <div className="mt-1 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="flex flex-wrap items-center gap-2 font-mono text-[11px] text-gray-800">
                        {selectedLog.promptSource.usedFallback && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                            Fallback (built-in defaults)
                          </span>
                        )}
                        {selectedLog.promptSource.orchestratorSlug && (
                          <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700">
                            Orchestrator: {selectedLog.promptSource.orchestratorSlug}
                          </span>
                        )}
                        {selectedLog.promptSource.agentSlug && (
                          <span className="rounded bg-purple-50 px-1.5 py-0.5 text-[10px] text-purple-700">
                            Agent: {selectedLog.promptSource.agentSlug}
                          </span>
                        )}
                        {selectedLog.promptSource.agentPromptOrigin && (
                          <span className="text-gray-500">
                            ({selectedLog.promptSource.agentPromptOrigin})
                          </span>
                        )}
                        {selectedLog.promptSource.agentPromptVersion !== undefined && (
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
                            v{selectedLog.promptSource.agentPromptVersion}
                          </span>
                        )}
                      </div>

                      {selectedLog.promptSource.guardrailIds && selectedLog.promptSource.guardrailIds.length > 0 && (
                        <div>
                          <span className="text-[10px] uppercase text-gray-400">Applied Guardrails</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {selectedLog.promptSource.guardrailIds.map((gid, i) => (
                              <span
                                key={`${gid}-${i}`}
                                className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] text-emerald-700"
                              >
                                {gid}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {(!selectedLog.promptSource.guardrailIds || selectedLog.promptSource.guardrailIds.length === 0) && !selectedLog.promptSource.usedFallback && (
                        <div className="text-[10px] text-gray-400">
                          No guardrails were applied to this run.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedLog.policyViolation && (
                  <div className="rounded-lg bg-red-50 p-3 text-red-800 font-semibold">
                    Policy Violation: {selectedLog.policyViolation}
                  </div>
                )}
              </div>

              <div className="mt-6 shrink-0 text-right">
                <button
                  onClick={() => { setSelectedLog(null); setDetailLoading(false) }}
                  className="rounded-lg bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </RequirePermission>
  )
}
